import { Fragment, useState } from "react";
import { useParams } from "react-router";
import type { GapList as GapListSchema } from "@acto/genui-components-schema";
import { ChevronRightThin } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { ProductOrderBy } from "@acto/gql/graphql";
import { asCurrencyCode } from "@acto/taskpane/core/utils/currencyCode";
import { formatCurrencyDelta, formatKpiDelta, formatPercentDelta } from "@acto/taskpane/field-sales-2/utils/format";
import {
  Card,
  Header,
  CardHeadline,
  HoverRowList,
  InsetRowDivider,
  MetaLabel,
  RowBadge,
  RowBody,
  RowChevron,
  RowMeta,
  RowTitle,
  Section,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { ShellOverlay } from "@acto/taskpane/field-sales-2/shell";
import {
  ProductDrillOverlay,
  type ProductDrillTarget,
} from "@acto/taskpane/field-sales-2/components/patterns/product-drilldown";
import { DOMAIN_KPI } from "./domainKpi";
import { MeetingPrepRowItem } from "./rowPrimitives";

type GapPolarity = "negative" | "positive" | "mixed";

// Sentinel the producer emits when a row could not be resolved to a product.
const NIL_PRODUCT_ID = "00000000-0000-0000-0000-000000000000";

type GapItem = GapListSchema["items"][number];

const hasDelta = (item: GapItem): boolean => item.percentDelta !== 0;

const productsOf = (item: GapItem) => (item.products?.length ? item.products : [item.product]);

const isDrillableGroup = (product: GapItem["product"]) => product.isGroup === true && product.id !== NIL_PRODUCT_ID;

const polarityFromItems = (items: GapListSchema["items"]): GapPolarity => {
  const positives = items.filter((i) => i.absoluteDelta > 0).length;
  const negatives = items.filter((i) => i.absoluteDelta < 0).length;

  if (positives > 0 && negatives === 0) return "positive";
  if (negatives > 0 && positives === 0) return "negative";

  return "mixed";
};

// The gap badge takes one list-level direction; "mixed" maps to the neutral RowBadge tone.
const toneFromPolarity = (polarity: GapPolarity): "positive" | "negative" | "neutral" =>
  polarity === "mixed" ? "neutral" : polarity;

interface GapListProps {
  part: GapListSchema;
}

export function GapList({ part }: GapListProps) {
  const { locale } = useLocale();
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.gapList");
  const tPrep = useTranslations("fieldSalesTwo.genuiMeetingPrep");
  const { id: clientId } = useParams<{ id: string }>();
  const [drillTarget, setDrillTarget] = useState<ProductDrillTarget | null>(null);

  const polarity = polarityFromItems(part.items.filter(hasDelta));

  const kpiPeriod = part.kpiPeriod;
  const kpi = kpiPeriod ? DOMAIN_KPI[kpiPeriod.kpi] : null;
  const currency = asCurrencyCode(part.currency);
  const formatDelta = (value: number) =>
    kpi ? formatKpiDelta(kpi, value, currency, locale) : formatCurrencyDelta(value, currency, locale);

  // kpiPeriod states what the deltas measure and over which period, so without it there is nothing
  // to scope a drill to.
  const rowsDrillable = !!kpiPeriod && !!clientId;

  const startDrill = (item: GapItem) => {
    if (!kpiPeriod || !kpi || !clientId) return;

    setDrillTarget({
      clientId,
      eyebrow: tPrep("reasoningMeta"),
      label: item.product.name,
      parentGroupIds: productsOf(item).map((product) => product.id),
      kpi,
      dateWindow: kpiPeriod.dateRange,
      order: item.absoluteDelta > 0 ? ProductOrderBy.LargestGrowth : ProductOrderBy.LargestDecline,
    });
  };

  const closeDrill = () => setDrillTarget(null);

  return (
    <>
      {drillTarget && (
        <ShellOverlay id="gapListProductDrill" label={drillTarget.label} onDismiss={closeDrill}>
          <ProductDrillOverlay target={drillTarget} onClose={closeDrill} />
        </ShellOverlay>
      )}
      <Card bleed>
        <Section>
          <Header>
            <MetaLabel>{tPrep("reasoningMeta")}</MetaLabel>
            <CardHeadline>{part.headline}</CardHeadline>
          </Header>
        </Section>
        <Divider />
        <Section>
          <MetaLabel>{t("productsMeta")}</MetaLabel>
          <HoverRowList role="list">
            {part.items.map((item, index) => {
              const canDrill = rowsDrillable && productsOf(item).every(isDrillableGroup);

              return (
                <Fragment key={index}>
                  <MeetingPrepRowItem clickable={canDrill} onClick={canDrill ? () => startDrill(item) : undefined}>
                    {hasDelta(item) && (
                      <RowBadge tone={toneFromPolarity(polarity)}>
                        {formatPercentDelta(item.percentDelta, locale)}
                      </RowBadge>
                    )}
                    <RowBody>
                      <RowTitle>{item.product.name}</RowTitle>
                      <RowMeta>{formatDelta(item.absoluteDelta)}</RowMeta>
                    </RowBody>
                    {canDrill && (
                      <RowChevron>
                        <ChevronRightThin strokeWidth={1.5} />
                      </RowChevron>
                    )}
                  </MeetingPrepRowItem>
                  {index < part.items.length - 1 && (hasDelta(item) ? <InsetRowDivider /> : <Divider />)}
                </Fragment>
              );
            })}
          </HoverRowList>
        </Section>
      </Card>
    </>
  );
}
